import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { formatApiError } from "@/lib/errors";

function mapError(e) {
  const status = e?.response?.status;
  if (status === 401) return "Invalid email or password.";
  if (status === 403) return "Account disabled, contact admin.";
  return formatApiError(e, "Something went wrong. Please try again.");
}

export default function Login() {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isLoading && user) {
      const to = location.state?.from || "/";
      navigate(to, { replace: true });
    }
  }, [user, isLoading, location.state, navigate]);

  if (!isLoading && user) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email: email.trim(), password, remember });
      toast.success("Welcome back");
      const to = location.state?.from || "/";
      navigate(to, { replace: true });
    } catch (err) {
      setError(mapError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-100 via-white to-slate-50 px-4 py-10"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-slate-900/5 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 right-10 h-96 w-96 rounded-full bg-cargodash-action/10 blur-3xl"
      />

      <div className="relative w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-[11px] font-bold text-amber-300 shadow-sm ring-1 ring-amber-400/40">
            BS
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-semibold tracking-tight text-slate-900">
              BlackScott Hub
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              Internal · Staff access
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
          <div className="mb-6">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
              Sign in
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Sign in with your work email to access your tenant workspaces.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="space-y-5"
            data-testid="login-form"
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@blackscott.co.za"
                data-testid="login-email"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <span className="text-[11px] text-slate-400">
                  Reset arrives in Phase 2
                </span>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                className="h-11"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={(v) => setRemember(Boolean(v))}
                data-testid="login-remember"
              />
              <span>Remember me for 30 days</span>
            </label>

            {error && (
              <div
                data-testid="login-error"
                role="alert"
                className="flex items-start gap-2 rounded-md border border-cargodash-alert-600/20 bg-cargodash-alert-600/5 px-3 py-2 text-sm text-cargodash-alert-600"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !email || !password}
              data-testid="login-submit"
              className="h-11 w-full bg-slate-900 font-semibold text-white transition-colors hover:bg-slate-800 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-400"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </div>

        <p className="mt-5 text-center text-xs text-slate-400">
          Authorised staff only ·{" "}
          <Link to="/" className="underline-offset-2 hover:underline">
            BlackScott Holdings
          </Link>
        </p>
      </div>
    </div>
  );
}
